import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cond from 'lodash/cond';

import { SuiModal, SuiModalContent } from '@splashthat/sui';
import CubeLoaderOverlay from 'common/components/CubeLoaderOverlay';
import { MODAL_SIZE } from 'common/components/CubeLoader';
import { colorAppShadedBackground } from 'common/styles/colors';
import SaveModalFooter from 'common/components/SaveModalFooter';
import { getIsRegistrationUpdatingEnabled } from 'common/entities/event/selectors';

import ScaledFormsList from './ScaledFormsList';
import { getFormLibrarySearchTerm, getFormLibraryStatus, getFormLibraryName } from '../../selectors/formLibrary';
import { applyFormToEventEffect, applyFormWithFormIdToEventEffect, searchForm } from '../../epics/formLibraryEffects';
import ScaledFormSearchInput from './ScaledFormSearchInput';
import { setFormLibrarySearchTerm } from '../../actions/formLibrary';
import ScaledFormsConfirmationContent from './ScaledFormsConfirmationContent';
import { APPLYING } from '../../reducers/formLibrary';
import ScaledFormsModalHeader from './ScaledFormsModalHeader';

const ModalContainer = styled(SuiModalContent)`
    .SuiContainer--padded {
        padding: 0;
    }
    .SuiRadioGroup-option {
        padding: 0;
    }
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow-x: hidden;
    height: 67vh;
`;

const ScaledFormsLibraryContainer = styled(SuiModalContent)`
    height: 100%;
`;

const MessageContainer = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    white-space: pre;
    justify-content: center;
    text-align: center;
`;

const SCALED_FORMS_MODAL_TITLE = 'Choose form from library';
const APPLY_FORM = 'applyForm';
const APPLY_CONFIRMATION = 'applyConfirmation';
const APPLY_CONFIRMATION_TITLE = 'Confirm form template choice';
const LIBRARY_LIST_COMPONENT = 'libraryListComponent';
const MODAL_CANCEL_BUTTON_LABEL = 'Cancel';
const GO_BACK_BUTTON_LABEL = 'Go Back';
const APPLY_LABEL = 'Apply Form';
const CONFIRM_LABEL = 'Confirm';
export const CANNOT_SELECT_FORM_FROM_LIBRARY_MESSAGE =
    'This event allows attendees to update their RSVP responses \ntherefore you cannot choose a new form from the library.';

const headlineTitles = {
    [APPLY_CONFIRMATION]: APPLY_CONFIRMATION_TITLE,
    [LIBRARY_LIST_COMPONENT]: SCALED_FORMS_MODAL_TITLE,
};

const COMPONENT_NAME = 'ScaledFormLibraryModal';

const ScaledFormsLibraryModal = (props) => {
    const [selectedForm, setSelectedForm] = useState(null);
    const [shownModalContent, setShownModalContent] = useState(LIBRARY_LIST_COMPONENT);
    const formName = useSelector((state) => getFormLibraryName(state, selectedForm));
    const dispatch = useDispatch();
    const status = useSelector(getFormLibraryStatus);
    const searchTerm = useSelector(getFormLibrarySearchTerm);
    const canModifyRsvp = useSelector(getIsRegistrationUpdatingEnabled);
    const { formId } = useParams() || {};

    const closeModal = () => {
        setSelectedForm(null);
        props.onClose();
    };

    const handleSearchTerm = (term) => {
        if (searchTerm !== term) {
            dispatch(setFormLibrarySearchTerm(term));
            dispatch(searchForm(term));
        }
    };

    const handleApplyForm = () => {
        if (formId) {
            dispatch(applyFormWithFormIdToEventEffect({ formId, formLibraryFormId: selectedForm }));
        } else {
            dispatch(applyFormToEventEffect(selectedForm));
        }
    };

    const renderLibraryContent = () => (
        <ScaledFormsLibraryContainer>
            <ScaledFormSearchInput searchTerm={searchTerm} onSearch={handleSearchTerm} />
            <ScaledFormsList
                setSelectedForm={setSelectedForm}
                selectedForm={selectedForm}
                status={status}
                searchTerm={searchTerm}
            />
        </ScaledFormsLibraryContainer>
    );
    const renderCannotChooseFormFromLibrary = () => (
        <MessageContainer data-qa={`${COMPONENT_NAME}__cannot-select-form-message`}>
            {CANNOT_SELECT_FORM_FROM_LIBRARY_MESSAGE}
        </MessageContainer>
    );

    return (
        <SuiModal
            open={props.open}
            size={SuiModal.MEDIUM}
            useClickOutside
            onRequestClose={closeModal}
            showCloseButton={false}
            heightMatchContent
        >
            <ModalContainer variation="border standard flex-vertical">
                <ScaledFormsModalHeader headlineTitle={headlineTitles[shownModalContent]} headlineSize="xl" />
                <ScaledFormsLibraryContainer>
                    {cond([
                        [() => canModifyRsvp === true, renderCannotChooseFormFromLibrary],
                        [
                            () => shownModalContent === APPLY_CONFIRMATION && status !== APPLYING,
                            () => (
                                <ScaledFormsConfirmationContent
                                    shownModalContent={shownModalContent}
                                    formName={formName}
                                />
                            ),
                        ],
                        [
                            () => status === APPLYING,
                            () => (
                                <CubeLoaderOverlay
                                    backgroundColor={colorAppShadedBackground}
                                    size={MODAL_SIZE}
                                    delay={0}
                                    data-qa={`${COMPONENT_NAME}__loader`}
                                />
                            ),
                        ],
                        [() => true, renderLibraryContent],
                    ])()}
                </ScaledFormsLibraryContainer>
                <SaveModalFooter
                    onCancel={() =>
                        shownModalContent === LIBRARY_LIST_COMPONENT
                            ? closeModal()
                            : setShownModalContent(LIBRARY_LIST_COMPONENT)
                    }
                    isMainComponent={shownModalContent === LIBRARY_LIST_COMPONENT}
                    status={status}
                    onConfirm={() =>
                        shownModalContent === LIBRARY_LIST_COMPONENT
                            ? setShownModalContent(APPLY_CONFIRMATION)
                            : handleApplyForm()
                    }
                    selectedValue={shownModalContent === LIBRARY_LIST_COMPONENT ? APPLY_FORM : null}
                    disabledConfirmButton={!selectedForm}
                    confirmButtonLabel={shownModalContent === LIBRARY_LIST_COMPONENT ? APPLY_LABEL : CONFIRM_LABEL}
                    cancelButtonLabel={
                        shownModalContent === LIBRARY_LIST_COMPONENT ? MODAL_CANCEL_BUTTON_LABEL : GO_BACK_BUTTON_LABEL
                    }
                />
            </ModalContainer>
        </SuiModal>
    );
};

ScaledFormsLibraryModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default ScaledFormsLibraryModal;
